import React, { useState, useEffect } from "react";
import useUserStore from "../stores/useUserStore";
import { useApi, getAccessToken } from "../apiHelpers";
import { useToast } from "../components/Toast/ToastContext";
import { setStatus } from "../utils/flag-utils";
import config from "../config";
import { buttonClass } from "../components/ui/Button";
import { Dialog } from "../components/ui/Dialog";
import LogoSpinner from "../components/ui/LogoSpinner";
import { Tag } from "../components/ui/Tag";
import { Field } from "../components/ui/Field";
import { Chip, ChipGroup } from "../components/ui/Chip";
import { DropZone } from "../components/ui/DropZone";

const FlagPopUp = ({ drone_serial, statusdesc, descript: initialDescript, fullFlag, drone, updateState, closePopup, droneTestFail }) => {
    const [descript, setDescript] = useState(initialDescript || "");
    const [logFile, setLogFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const api = useApi();
    const toast = useToast();
    const user = useUserStore((state) => state.user);

    const isOOC = statusdesc === "OOC";

    useEffect(() => {
        if (initialDescript) {
            setDescript(initialDescript);
        }
    }, [initialDescript]);

    const handleSubmit = async () => {
        setIsSubmitting(true);

        try {
            let flagId = fullFlag?.id;

            if (fullFlag) {
                // TODO: Consider adding updated_by field to track who edited
                await api.patch(`/flags/entry/${fullFlag.id}`, {
                    body: {
                        descript,
                        statusdesc
                    },
                    errorAlert: "Can't update flag, please try again"
                });
            } else {
                // user_id is automatically taken from JWT on the backend
                const result = await setStatus(api, drone_serial, descript, statusdesc);
                flagId = result?.flag?.id;
            }

            if (logFile && flagId) {
                try {
                    const formData = new FormData();
                    formData.append('file', logFile);
                    formData.append('drone_serial', drone_serial);
                    formData.append('source_type', 'flag');
                    formData.append('source_id', flagId);
                    formData.append('notes', `Uploaded with flag: ${descript}`);

                    await fetch(`${config.API_ENDPOINT}/logs/upload`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${getAccessToken()}`
                        },
                        body: formData
                    });
                } catch (logErr) {
                    console.error('Log upload error:', logErr);
                }
            }

            updateState(statusdesc);
            closePopup();
        } catch (error) {
            if (error.message?.includes('already has an active')) {
                toast.warning(error.message);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFileChange = (file) => {
        if (file && file.name.toLowerCase().endsWith('.ulg')) {
            setLogFile(file);
        } else {
            setLogFile(null);
        }
    };

    const categories = [
        "ESC or Motor",
        "LED board",
        "AutoPilot Issues",
        "GPS Issues",
        "LoRa Issues",
        "No Uploads",
        "Hard Landing",
        "Dark Fall",
        "Connection",
        "Fire/Shorts",
        "Vibrations",
        "Check Props",
    ];

    const shortSerial = drone_serial;

    return (
        <Dialog
            open
            onClose={closePopup}
            size="lg"
            busy={isSubmitting}
            dirty={descript !== (initialDescript || "") || !!logFile}
            onSubmit={handleSubmit}
            label={`${fullFlag ? "Edit flag" : "Create flag"} for ${shortSerial}`}
            title={
                <span className="flex items-center gap-3">
                    <Tag tone={isOOC ? "bad" : "accent"}>{statusdesc}</Tag>
                    <span>{shortSerial}</span>
                </span>
            }
            meta={fullFlag ? "Edit flag details" : "Create new flag"}
            footer={
                <>
                    <button
                        type="button"
                        onClick={closePopup}
                        disabled={isSubmitting}
                        className={buttonClass("quiet", "lg")}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={isSubmitting || !descript.trim()}
                        aria-busy={isSubmitting || undefined}
                        className={buttonClass("filled", "lg")}
                    >
                        {isSubmitting ? (
                            <span className="flex items-center gap-2">
                                <LogoSpinner size="inline" />
                                Saving...
                            </span>
                        ) : (
                            fullFlag ? "Update Flag" : "Create Flag"
                        )}
                    </button>
                </>
            }
        >
            <div className="space-y-4">
                {/* Description Input */}
                <div>
                    <label htmlFor="flag-reason" className="block text-sm font-medium text-on-surface-variant mb-2">
                        Reason for {statusdesc}
                    </label>
                    <Field
                        id="flag-reason"
                        type="text"
                        value={descript}
                        onChange={(e) => setDescript(e.target.value)}
                        placeholder="Enter description or select below..."
                        autoFocus
                    />
                </div>

                {/* Quick Categories */}
                <div>
                    <label className="block text-sm font-medium text-on-surface-variant mb-2">
                        Quick Select
                    </label>
                    <ChipGroup label="Quick select">
                        {categories.map((category) => (
                            <Chip
                                key={category}
                                pressed={descript === category}
                                onClick={() => setDescript(category)}
                            >
                                {category}
                            </Chip>
                        ))}
                    </ChipGroup>
                </div>

                {/* Flight Log Upload */}
                <div>
                    <label className="block text-sm font-medium text-on-surface-variant mb-2">
                        Attach Flight Log
                        <span className="text-on-surface-variant font-normal ml-1">(optional)</span>
                    </label>
                    <DropZone
                        what=".ulg file"
                        accept=".ulg"
                        size="sm"
                        chosen={logFile}
                        onClear={() => setLogFile(null)}
                        onFiles={(files) => handleFileChange(files[0])}
                    />
                </div>
            </div>
        </Dialog>
    );
};

export default FlagPopUp;
